/**
 * Custom hook for managing QuickSight dashboard AI summary generation
 * Combines dashboard event handling and summary generation logic
 */

import { useRef, useCallback, useEffect, useState } from 'react';
import { findPayload } from '@execonline-inc/collections';
import { DashboardResource, ReportResource } from '../../../ReportStore/Types';
import DashboardSummaryStore from '../../../DashboardSummaryStore';
import { just, nothing } from 'maybeasy';
import {
  generateQuickSightSummary,
  QuickSightSummaryResponse,
  QuickSightSummaryMetadata,
} from '../../../services/quicksightSummaryService';
import {
  DashboardSummaryMetadata,
  DashboardSummaryFilters,
} from '../../../DashboardSummaryStore/Types';
import { findLink } from '../../../LinkyLinky';
import { getDashboardParameters } from '../../Visualization/quickSightEmbedAPILoader';
import {
  extractDashboardId,
  extractFiltersFromParameters,
  filtersHaveChanged,
  checkForActionPlanSheet,
} from './utils';

// Timing constants
const POLL_INTERVAL_MS = 3000;
const SHEET_CHECK_DELAY_MS = 2000;
const INITIAL_LOAD_DELAY_MS = 1500;
const PARAMETER_CHANGE_GRACE_PERIOD_MS = 5000;
const PARAMETER_CHANGE_DEBOUNCE_MS = 2000;
const MAX_PARAMETER_RETRIES = 10; // Max retries before giving up (30 seconds total)

/**
 * Interface for QuickSight dashboard object returned by the embedding SDK
 */
interface QuickSightDashboard {
  addEventListener?: (event: string, handler: () => void) => void;
  removeEventListener?: (event: string, handler: () => void) => void;
  on?: (event: string, handler: () => void) => void;
  off?: (event: string, handler: () => void) => void;
  getSheets?: () => Promise<Array<{ Name: string; SheetId: string }>>;
}

/**
 * Registered event listener for cleanup tracking
 */
interface RegisteredListener {
  event: string;
  handler: () => void;
}

interface UseQuickSightSummaryProps {
  currentDashboardId: number;
  dashboards: DashboardResource[];
  reportResource: ReportResource;
  summaryStore: DashboardSummaryStore;
}

interface UseQuickSightSummaryReturn {
  hasActionPlanSheet: boolean;
  handleDashboardReady: (dashboard: any) => void;
  handleRefreshSummary: () => void;
  resetForDashboardChange: () => void;
  cleanup: () => void;
}

export const useQuickSightSummary = ({
  currentDashboardId,
  dashboards,
  reportResource,
  summaryStore,
}: UseQuickSightSummaryProps): UseQuickSightSummaryReturn => {
  const [hasActionPlanSheet, setHasActionPlanSheet] = useState(false);

  // Refs for tracking state
  const dashboardRef = useRef<QuickSightDashboard | null>(null);
  const previousFiltersRef = useRef<Record<string, string>>({});
  const parameterRetryCountRef = useRef<number>(0);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const forceRefreshRef = useRef<boolean>(false); // Track if any debounced call needs force refresh
  const requestIdRef = useRef<number>(0);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const currentTaskRef = useRef<(() => void) | null>(null);
  const initialLoadCompleteRef = useRef<boolean>(false);
  const hasTriggeredInitialLoadRef = useRef<boolean>(false);
  const ignoreParameterChangesUntilRef = useRef<number>(0);
  const isMountedRef = useRef<boolean>(true);
  const apiCallInProgressRef = useRef<boolean>(false); // Prevent duplicate API calls

  // Refs for tracking timeouts and listeners for cleanup
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const sheetCheckTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const initialLoadTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const registeredListenersRef = useRef<RegisteredListener[]>([]);
  const isTabVisibleRef = useRef<boolean>(true); // Track tab visibility for polling optimization

  // Track mounted state for cleanup
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Track tab visibility to pause polling when tab is hidden (performance optimization)
  useEffect(() => {
    const handleVisibilityChange = () => {
      isTabVisibleRef.current = document.visibilityState === 'visible';
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const processApiResponse = useCallback(
    (
      data: QuickSightSummaryResponse,
      quicksightDashboardId: string,
      filters: Record<string, string>,
      store: DashboardSummaryStore,
    ) => {
      if (!isMountedRef.current) return;

      const displayFilters: DashboardSummaryFilters = {
        startDate: just(filters.start_date),
        endDate: just(filters.end_date),
      };

      if (data.status === 'success') {
        const metadata: DashboardSummaryMetadata = data.metadata.cata({
          Just: (m: QuickSightSummaryMetadata) => ({
            dashboardId: m.dashboardId,
            tabId: m.tabId,
            generatedAt: m.generatedAt,
          }),
          Nothing: () => ({
            dashboardId: quicksightDashboardId,
            tabId: nothing<string>(),
            generatedAt: new Date().toISOString(),
          }),
        });

        store.setSuccess({
          summary: data.summary,
          headline: data.headline,
          rationale: just(data.rationale),
          fullAnalysis: data.fullAnalysis,
          metadata: metadata,
          filters: displayFilters,
        });
      } else if (data.status === 'empty') {
        const message = data.message.getOrElse(
          () =>
            "No action plan data is available for analysis yet. Once participants complete action plans, you'll see an AI-powered summary here.",
        );
        store.setEmpty(message, displayFilters);
      } else {
        store.setFailed(data.error.getOrElse(() => 'Failed to generate summary'));
      }
    },
    [],
  );

  const callSummaryAPI = useCallback(
    (
      quicksightDashboardId: string,
      filters: Record<string, string>,
      requestId: number,
      onComplete?: () => void,
    ) => {
      if (currentTaskRef.current) {
        currentTaskRef.current();
        currentTaskRef.current = null;
      }

      // Mark API call as in progress to prevent duplicate calls
      apiCallInProgressRef.current = true;

      findLink('impact-report-summaries', reportResource.links)
        .map((link) => {
          const summaryTask = generateQuickSightSummary(link, {
            dashboardId: quicksightDashboardId,
            locale: 'en',
            source: 'reports',
            filters: filters,
          });

          const cancelTask = summaryTask.fork(
            (error) => {
              currentTaskRef.current = null;
              apiCallInProgressRef.current = false;
              if (!isMountedRef.current) return;

              const errorMessage =
                error.kind === 'bad-status'
                  ? `Unable to generate summary (Server error ${error.response.status}). Please try again later.`
                  : error.kind === 'timeout'
                    ? 'Request timed out. Please check your connection and try again.'
                    : error.kind === 'network-error'
                      ? 'Network error. Please check your internet connection.'
                      : 'Failed to generate summary. Please try again.';
              summaryStore.setFailed(errorMessage);
            },
            (data) => {
              currentTaskRef.current = null;
              apiCallInProgressRef.current = false;
              if (!isMountedRef.current) return;
              if (requestId !== requestIdRef.current) return;
              processApiResponse(data, quicksightDashboardId, filters, summaryStore);
              onComplete?.();
            },
          );

          currentTaskRef.current = cancelTask;
        })
        .elseDo(() => {
          apiCallInProgressRef.current = false;
          if (isMountedRef.current) {
            summaryStore.setFailed('impact-report-summaries link not found');
          }
        });
    },
    [reportResource.links, summaryStore, processApiResponse],
  );

  /**
   * Generates an AI summary for the current dashboard filters.
   * @param skipChangeCheck - If true, bypasses filter change detection and forces regeneration
   * @param debounceMs - Delay in milliseconds before executing (0 for immediate)
   * @param onComplete - Optional callback invoked after successful API response
   */
  const generateSummary = useCallback(
    (skipChangeCheck: boolean = false, debounceMs: number = 0, onComplete?: () => void) => {
      // Prevent duplicate API calls - if one is already in progress, skip entirely
      // This prevents race conditions where multiple calls try to start simultaneously
      if (apiCallInProgressRef.current) {
        return;
      }

      // Track if any call during debounce period needs force refresh
      if (skipChangeCheck) {
        forceRefreshRef.current = true;
      }

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }

      if (debounceMs > 0) {
        debounceTimerRef.current = setTimeout(() => {
          // Use forceRefreshRef to preserve skipChangeCheck=true from any debounced call
          const shouldForceRefresh = forceRefreshRef.current;
          forceRefreshRef.current = false;
          generateSummary(shouldForceRefresh, 0, onComplete);
        }, debounceMs);
        return;
      }

      // Reset force refresh flag when actually executing
      forceRefreshRef.current = false;

      if (!isMountedRef.current) return;

      findPayload(currentDashboardId, dashboards)
        .map((dashboard) => {
          const quicksightDashboardId = extractDashboardId(dashboard.payload.dashboardUri);

          if (!quicksightDashboardId) {
            // Set loading first so setFailed transition works
            summaryStore.startLoading(currentDashboardId.toString(), undefined);
            summaryStore.setFailed('Could not extract QuickSight dashboard ID from URL');
            return;
          }

          if (!dashboardRef.current) {
            // Set loading first so setFailed transition works
            summaryStore.startLoading(currentDashboardId.toString(), undefined);
            summaryStore.setFailed('Dashboard not ready');
            return;
          }

          getDashboardParameters(dashboardRef.current).fork(
            () => {
              // Parameters API failed - retry after a delay with limit
              parameterRetryCountRef.current += 1;
              if (parameterRetryCountRef.current >= MAX_PARAMETER_RETRIES) {
                summaryStore.startLoading(currentDashboardId.toString(), undefined);
                summaryStore.setFailed(
                  'Unable to retrieve dashboard parameters. Please refresh the page.',
                );
                return;
              }
              // Clear any existing retry timeout before setting a new one
              if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
              }
              retryTimeoutRef.current = setTimeout(() => {
                if (isMountedRef.current) {
                  generateSummary(skipChangeCheck, 0, onComplete);
                }
              }, POLL_INTERVAL_MS);
            },
            (parameters) => {
              // For Reports page, always wait for QuickSight to provide its actual parameters
              // Don't use fallback defaults - QuickSight is the source of truth for date filters
              extractFiltersFromParameters(parameters, false).cata({
                Nothing: () => {
                  // QuickSight hasn't provided date parameters yet - retry with limit
                  parameterRetryCountRef.current += 1;
                  if (parameterRetryCountRef.current >= MAX_PARAMETER_RETRIES) {
                    summaryStore.startLoading(currentDashboardId.toString(), undefined);
                    summaryStore.setFailed(
                      'Dashboard date parameters not available. Please ensure the dashboard has date filters configured.',
                    );
                    return;
                  }
                  // Clear any existing retry timeout before setting a new one
                  if (retryTimeoutRef.current) {
                    clearTimeout(retryTimeoutRef.current);
                  }
                  retryTimeoutRef.current = setTimeout(() => {
                    if (isMountedRef.current) {
                      generateSummary(skipChangeCheck, 0, onComplete);
                    }
                  }, POLL_INTERVAL_MS);
                },
                Just: (cleanedFilters) => {
                  // Reset retry count on success
                  parameterRetryCountRef.current = 0;

                  // Only proceed if filters changed or we're forcing refresh
                  if (
                    !skipChangeCheck &&
                    !filtersHaveChanged(previousFiltersRef.current, cleanedFilters)
                  ) {
                    // Filters haven't changed and not forcing - skip API call
                    // Don't change state since we're not actually doing anything
                    return;
                  }

                  // NOW we know we're making an API call, so set loading state and increment request ID
                  summaryStore.startLoading(currentDashboardId.toString(), undefined);
                  requestIdRef.current += 1;
                  const currentRequestId = requestIdRef.current;

                  previousFiltersRef.current = { ...cleanedFilters };
                  callSummaryAPI(
                    quicksightDashboardId,
                    cleanedFilters,
                    currentRequestId,
                    onComplete,
                  );
                },
              });
            },
          );
        })
        .elseDo(() => {
          // Set loading first so setFailed transition works
          summaryStore.startLoading(currentDashboardId.toString(), undefined);
          summaryStore.setFailed('Dashboard not found');
        });
    },
    // Refs (forceRefreshRef, debounceTimerRef, dashboardRef, etc.) are intentionally excluded
    // from dependencies as they are stable across renders and don't trigger re-renders
    [currentDashboardId, dashboards, summaryStore, callSummaryAPI],
  );

  const startPollingForChanges = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    pollIntervalRef.current = setInterval(() => {
      // Skip polling when tab is hidden to reduce unnecessary work
      if (!dashboardRef.current || !isMountedRef.current || !isTabVisibleRef.current) {
        return;
      }

      getDashboardParameters(dashboardRef.current).fork(
        () => {
          // Ignore polling errors
        },
        (parameters) => {
          extractFiltersFromParameters(parameters).cata({
            Nothing: () => {
              // Skip if QuickSight hasn't provided date parameters yet
            },
            Just: (cleanedFilters) => {
              if (
                initialLoadCompleteRef.current &&
                Date.now() > ignoreParameterChangesUntilRef.current &&
                filtersHaveChanged(previousFiltersRef.current, cleanedFilters)
              ) {
                generateSummary(false, PARAMETER_CHANGE_DEBOUNCE_MS);
              }
            },
          });
        },
      );
    }, POLL_INTERVAL_MS);
  }, [generateSummary]);

  const handleDashboardReady = useCallback(
    (dashboard: QuickSightDashboard) => {
      if (hasTriggeredInitialLoadRef.current) {
        return;
      }
      hasTriggeredInitialLoadRef.current = true;

      dashboardRef.current = dashboard;

      // Check if the dashboard has an Action Plan Impact sheet
      if (sheetCheckTimeoutRef.current) {
        clearTimeout(sheetCheckTimeoutRef.current);
      }
      sheetCheckTimeoutRef.current = setTimeout(() => {
        if (!isMountedRef.current) return;
        checkForActionPlanSheet(dashboard)
          .then((hasActionPlan) => {
            if (isMountedRef.current) {
              setHasActionPlanSheet(hasActionPlan);
            }
          })
          .catch(() => {
            // Error already handled in checkForActionPlanSheet
          });
      }, SHEET_CHECK_DELAY_MS);

      // Listen for parameter and sheet changes
      const addListener = dashboard.addEventListener || dashboard.on;

      if (addListener) {
        // Parameter change events - debounce these to let QuickSight update
        const parameterEventNames = ['PARAMETERS_CHANGED', 'PARAMETER_CHANGED', 'FILTER_CHANGED'];

        parameterEventNames.forEach((eventName) => {
          const handler = () => {
            if (
              isMountedRef.current &&
              initialLoadCompleteRef.current &&
              Date.now() > ignoreParameterChangesUntilRef.current
            ) {
              // Use longer debounce to ensure QuickSight has updated its parameters
              generateSummary(false, PARAMETER_CHANGE_DEBOUNCE_MS);
            }
          };

          try {
            addListener.call(dashboard, eventName, handler);
            // Track listener for cleanup
            registeredListenersRef.current.push({ event: eventName, handler });
          } catch {
            // Ignore registration errors
          }
        });

        // Sheet change event - regenerate immediately when user switches tabs
        const sheetChangeHandler = () => {
          if (isMountedRef.current && initialLoadCompleteRef.current) {
            // Ignore parameter changes for a grace period after sheet change
            // to prevent race conditions with parameter events that fire during sheet transition
            ignoreParameterChangesUntilRef.current = Date.now() + PARAMETER_CHANGE_GRACE_PERIOD_MS;

            // Force regenerate with skip change check since filters may be the same
            // but we want fresh data for the new sheet
            generateSummary(true, PARAMETER_CHANGE_DEBOUNCE_MS);
          }
        };

        try {
          addListener.call(dashboard, 'SHEET_CHANGED', sheetChangeHandler);
          // Track listener for cleanup
          registeredListenersRef.current.push({
            event: 'SHEET_CHANGED',
            handler: sheetChangeHandler,
          });
        } catch {
          // Ignore registration errors
        }
      }

      // Initial summary generation
      if (initialLoadTimeoutRef.current) {
        clearTimeout(initialLoadTimeoutRef.current);
      }
      initialLoadTimeoutRef.current = setTimeout(() => {
        if (!isMountedRef.current) return;
        generateSummary(true, 0, () => {
          initialLoadCompleteRef.current = true;
          ignoreParameterChangesUntilRef.current = Date.now() + PARAMETER_CHANGE_GRACE_PERIOD_MS;
          // Start polling AFTER initial load completes to avoid detecting initial state as a "change"
          // Events may not fire reliably for all parameter types, so polling is a fallback
          startPollingForChanges();
        });
      }, INITIAL_LOAD_DELAY_MS);
    },
    [generateSummary, startPollingForChanges],
  );

  const handleRefreshSummary = useCallback(() => {
    // Cancel any in-progress API call for user-triggered refresh
    if (currentTaskRef.current) {
      currentTaskRef.current();
      currentTaskRef.current = null;
    }
    apiCallInProgressRef.current = false;
    generateSummary(true, 0);
  }, [generateSummary]);

  const cleanup = useCallback(() => {
    // Clear all intervals
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    // Clear all timeouts
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
    if (sheetCheckTimeoutRef.current) {
      clearTimeout(sheetCheckTimeoutRef.current);
      sheetCheckTimeoutRef.current = null;
    }
    if (initialLoadTimeoutRef.current) {
      clearTimeout(initialLoadTimeoutRef.current);
      initialLoadTimeoutRef.current = null;
    }

    // Cancel any in-flight API tasks
    if (currentTaskRef.current) {
      currentTaskRef.current();
      currentTaskRef.current = null;
    }

    // Remove event listeners from dashboard
    if (dashboardRef.current && registeredListenersRef.current.length > 0) {
      const removeListener = dashboardRef.current.removeEventListener || dashboardRef.current.off;
      if (removeListener) {
        registeredListenersRef.current.forEach(({ event, handler }) => {
          try {
            removeListener.call(dashboardRef.current, event, handler);
          } catch {
            // Ignore removal errors
          }
        });
      }
      registeredListenersRef.current = [];
    }
  }, []);

  const resetForDashboardChange = useCallback(() => {
    cleanup();
    dashboardRef.current = null;
    initialLoadCompleteRef.current = false;
    hasTriggeredInitialLoadRef.current = false;
    ignoreParameterChangesUntilRef.current = 0;
    setHasActionPlanSheet(false);
    previousFiltersRef.current = {};
    parameterRetryCountRef.current = 0;
    forceRefreshRef.current = false;
    requestIdRef.current = 0;
    apiCallInProgressRef.current = false;
    // Note: registeredListenersRef is already cleared in cleanup()
    summaryStore.reset();
  }, [cleanup, summaryStore]);

  return {
    hasActionPlanSheet,
    handleDashboardReady,
    handleRefreshSummary,
    resetForDashboardChange,
    cleanup,
  };
};
